import { useState } from 'react';
import {
  CARD_SIZE_LIST,
  DPI,
  PAPER_SIZE_LIST,
  buildCustomCardSize,
  buildCustomPaperSize,
  mmToIn,
} from '../lib/constants';
import { effectiveCard, effectivePaper } from '../lib/geometry';
import type { CardSize, CardSizeId, Orientation, PaperSize } from '../types';

interface Props {
  cardSize: CardSize;
  paperSize: PaperSize;
  orientation: Orientation;
  onCardSizeChange: (size: CardSize) => void;
  onPaperSizeChange: (size: PaperSize) => void;
  onOrientationChange: (next: Orientation) => void;
}

type Units = 'in' | 'mm';

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

const fmtIn = (n: number) => (n % 1 === 0 ? `${n}` : `${n}`);

const dimsLine = (s: { widthIn: number; heightIn: number }) =>
  `${fmtIn(s.widthIn)} × ${fmtIn(s.heightIn)} in`;

const pxLine = (s: { widthPx: number; heightPx: number }) =>
  `${s.widthPx} × ${s.heightPx} px`;

export function SizePicker({
  cardSize,
  paperSize,
  orientation,
  onCardSizeChange,
  onPaperSizeChange,
  onOrientationChange,
}: Props) {
  const [cardUnits, setCardUnits] = useState<Units>('in');
  const [cardW, setCardW] = useState<number>(5);
  const [cardH, setCardH] = useState<number>(7);

  const [paperUnits, setPaperUnits] = useState<Units>('in');
  const [paperW, setPaperW] = useState<number>(11);
  const [paperH, setPaperH] = useState<number>(8.5);

  const isCustomCard = cardSize.id === 'custom';
  const isCustomPaper = paperSize.id === 'custom';

  const effC = effectiveCard(cardSize, orientation);
  const effP = effectivePaper(paperSize, orientation);
  const spreadDescription =
    orientation === 'portrait'
      ? `${fmtIn(effC.widthIn * 2)} × ${fmtIn(effC.heightIn)} in (two panels side-by-side)`
      : `${fmtIn(effC.widthIn)} × ${fmtIn(effC.heightIn * 2)} in (two panels stacked top/bottom)`;

  const pickCardPreset = (id: CardSizeId) => {
    const next = CARD_SIZE_LIST.find((c) => c.id === id);
    if (next) onCardSizeChange(next);
  };

  const applyCustomCard = (w: number, h: number) => {
    const widthIn = cardUnits === 'in' ? w : mmToIn(w);
    const heightIn = cardUnits === 'in' ? h : mmToIn(h);
    onCardSizeChange(buildCustomCardSize(widthIn, heightIn));
  };

  const applyCustomPaper = (w: number, h: number) => {
    const widthIn = paperUnits === 'in' ? w : mmToIn(w);
    const heightIn = paperUnits === 'in' ? h : mmToIn(h);
    onPaperSizeChange(buildCustomPaperSize(widthIn, heightIn));
  };

  return (
    <section className="card-section sizepicker">
      <h2>1. Card &amp; paper</h2>

      <div className="picker-block">
        <span className="picker-label">Orientation</span>
        <div className="orient-toggle" role="radiogroup" aria-label="Card orientation">
          <button
            type="button"
            role="radio"
            aria-checked={orientation === 'portrait'}
            className={`orient-opt${orientation === 'portrait' ? ' active' : ''}`}
            onClick={() => onOrientationChange('portrait')}
            title="Tall card, vertical fold (book style)"
          >
            <span className="orient-glyph" aria-hidden>▯</span>
            <span className="orient-text">
              <span className="orient-name">Portrait</span>
              <span className="orient-detail">tall · vertical fold</span>
            </span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={orientation === 'landscape'}
            className={`orient-opt${orientation === 'landscape' ? ' active' : ''}`}
            onClick={() => onOrientationChange('landscape')}
            title="Wide card, horizontal fold (top-fold style)"
          >
            <span className="orient-glyph" aria-hidden>▭</span>
            <span className="orient-text">
              <span className="orient-name">Landscape</span>
              <span className="orient-detail">wide · horizontal fold</span>
            </span>
          </button>
        </div>
        <p className="picker-hint">
          Switching orientation re-crops your uploaded images to the new aspect — the originals are kept.
          Use <em>Edit image</em> below each image to reposition, rotate, or flip.
        </p>
      </div>

      <div className="picker-block">
        <span className="picker-label">Card size</span>
        <div className="chip-row">
          {CARD_SIZE_LIST.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`chip chip-rich${cardSize.id === c.id ? ' chip-active' : ''}`}
              onClick={() => pickCardPreset(c.id)}
            >
              <span className="chip-label">{c.label}</span>
              <span className="chip-dims">{dimsLine(c)}</span>
            </button>
          ))}
          <button
            type="button"
            className={`chip chip-rich${isCustomCard ? ' chip-active' : ''}`}
            onClick={() => applyCustomCard(cardW, cardH)}
          >
            <span className="chip-label">Custom…</span>
            <span className="chip-dims">{isCustomCard ? dimsLine(cardSize) : 'enter dims'}</span>
          </button>
        </div>

        {isCustomCard && (
          <div className="custom-size-row">
            <label>
              Width
              <input
                type="number"
                min={0.1}
                max={30}
                step={0.1}
                value={cardW}
                onChange={(e) => {
                  const v = clamp(parseFloat(e.target.value) || 0, 0.1, 30);
                  setCardW(v);
                  applyCustomCard(v, cardH);
                }}
              />
            </label>
            <span className="times">×</span>
            <label>
              Height
              <input
                type="number"
                min={0.1}
                max={30}
                step={0.1}
                value={cardH}
                onChange={(e) => {
                  const v = clamp(parseFloat(e.target.value) || 0, 0.1, 30);
                  setCardH(v);
                  applyCustomCard(cardW, v);
                }}
              />
            </label>
            <div className="unit-toggle" role="radiogroup" aria-label="Card units">
              {(['in', 'mm'] as Units[]).map((u) => (
                <button
                  key={u}
                  type="button"
                  role="radio"
                  aria-checked={cardUnits === u}
                  className={`unit-btn${cardUnits === u ? ' unit-active' : ''}`}
                  onClick={() => setCardUnits(u)}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="picker-readout">
          Folded card ({orientation}): <strong>{dimsLine(effC)}</strong> ({pxLine(effC)} @ {DPI} DPI).
          <br />
          Spread on paper: <strong>{spreadDescription}</strong>.
        </p>
      </div>

      <div className="picker-block">
        <span className="picker-label">Paper</span>
        <div className="chip-row">
          {PAPER_SIZE_LIST.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`chip chip-rich${paperSize.id === p.id ? ' chip-active' : ''}`}
              onClick={() => onPaperSizeChange(p)}
            >
              <span className="chip-label">{p.label}</span>
              <span className="chip-dims">{dimsLine(p)}</span>
            </button>
          ))}
          <button
            type="button"
            className={`chip chip-rich${isCustomPaper ? ' chip-active' : ''}`}
            onClick={() => applyCustomPaper(paperW, paperH)}
          >
            <span className="chip-label">Custom…</span>
            <span className="chip-dims">{isCustomPaper ? dimsLine(paperSize) : 'any size'}</span>
          </button>
        </div>

        {isCustomPaper && (
          <div className="custom-size-row">
            <label>
              Width
              <input
                type="number"
                min={1}
                max={60}
                step={0.1}
                value={paperW}
                onChange={(e) => {
                  const v = clamp(parseFloat(e.target.value) || 0, 1, 60);
                  setPaperW(v);
                  applyCustomPaper(v, paperH);
                }}
              />
            </label>
            <span className="times">×</span>
            <label>
              Height
              <input
                type="number"
                min={1}
                max={60}
                step={0.1}
                value={paperH}
                onChange={(e) => {
                  const v = clamp(parseFloat(e.target.value) || 0, 1, 60);
                  setPaperH(v);
                  applyCustomPaper(paperW, v);
                }}
              />
            </label>
            <div className="unit-toggle" role="radiogroup" aria-label="Paper units">
              {(['in', 'mm'] as Units[]).map((u) => (
                <button
                  key={u}
                  type="button"
                  role="radio"
                  aria-checked={paperUnits === u}
                  className={`unit-btn${paperUnits === u ? ' unit-active' : ''}`}
                  onClick={() => setPaperUnits(u)}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="picker-readout">
          Paper used: <strong>{dimsLine(effP)}</strong> ({orientation === 'portrait' ? 'landscape' : 'portrait'} orientation, auto-flipped to match the card).
          {' '}Anything bigger than the spread works at <strong>100% / Actual size</strong>; extra paper just becomes margin.
        </p>
      </div>
    </section>
  );
}
